import React, { memo, useEffect, useMemo, useState } from 'react';

import { useUser } from '@explorer/stores';
import {
  LDSingleKindContext,
  useLDClient,
} from 'launchdarkly-react-client-sdk';

import { useDatadogRum } from '@crucible-risk/react-monitoring';

type LaunchDarklyWrapperContextValue = {
  initialized: boolean;
  anonymous: boolean;
  error?: Error;
};

export const LaunchDarklyWrapperContext =
  React.createContext<LaunchDarklyWrapperContextValue>({
    initialized: false,
    anonymous: true,
  });

/**
 * LaunchDarklyWrapper
 */
export const LaunchDarklyWrapper = memo(
  //
  function LaunchDarklyWrapper({
    children = null,
    enabled = true,
  }: LaunchDarklyWrapperProps) {
    const { addError } = useDatadogRum();

    const preInitialized = !enabled;

    const [initialized, setInitialized] = useState(preInitialized);
    const [anonymous, setAnonymous] = useState(true);
    const [error, setError] = useState<Error | undefined>(undefined);

    const ldClient = useLDClient();
    const { user } = useUser();

    useEffect(() => {
      let cancelled = false;

      if (!enabled) {
        return undefined;
      }

      if (!ldClient) {
        return undefined;
      }

      ldClient
        .waitForInitialization()
        .then(() => {
          if (!cancelled) {
            setInitialized(true);
          }
        })
        .catch((e) => {
          const ldError = new Error('LaunchDarkly failed to initialize');
          ldError.cause = e;
          addError(ldError);
          if (!cancelled) {
            setError(e);
          }
        });

      return () => {
        /* If this component is unmounted before the initialization call returns, we don't want to set the anonymous flag and cause React to throw a warning */
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addError, ldClient]);

    useEffect(() => {
      let cancelled = false;

      if (!ldClient) {
        return undefined;
      }

      if (!enabled) {
        return undefined;
      }

      const currentUser = ldClient.getContext();

      if (!currentUser.anonymous) {
        setAnonymous(false);
        return undefined;
      }

      /* Auth0 user is not fetched yet */
      if (!user?.sub) {
        return undefined;
      }

      /* Create a LaunchDarkly user based on the auth0 user info */
      const ldUser: LDSingleKindContext = {
        kind: 'user',
        name: user?.name,
        email: user?.email,
        key: user?.sub,
      };

      ldClient
        ?.identify(ldUser)
        .then(() => {
          if (!cancelled) {
            setAnonymous(false);
          }
        })
        .catch((e) => {
          if (cancelled) {
            return;
          }

          const ldError = new Error('LaunchDarkly failed to retrieve user');
          ldError.cause = e;
          addError(ldError, {
            cause: e,
          });
          setAnonymous(true);
          if (!cancelled) {
            setError(e);
          }
        });

      return () => {
        /* If this component is unmounted before the identify call returns, we don't want to set the anonymous flag and cause React to throw a warning */
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, ldClient]);

    const contextValue = useMemo(
      () => ({ initialized, anonymous, error }),
      [initialized, anonymous, error],
    );

    return (
      <LaunchDarklyWrapperContext.Provider value={contextValue}>
        {children}
      </LaunchDarklyWrapperContext.Provider>
    );
  },
);

LaunchDarklyWrapper.displayName = 'LaunchDarklyWrapper';

/**
 * LaunchDarklyWrapperProps
 */
export interface LaunchDarklyWrapperProps {
  enabled?: boolean;
  readonly children: JSX.Element[] | JSX.Element | null;
}
